'use no memo';
import { zodResolver } from '@hookform/resolvers/zod';
import { Requisition, ToolFundSettings, User } from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useAuth } from '../../../context/AuthContext';
import { useFeatureFlag } from '../../../featureFlags/useFeatureFlag';
import { useToolFundCatalogItemCategoryQuery } from '../../../hooks/react-query/useToolFundCatalogItemCategoryQuery';
import {
  useBatchGetRequisitionQuery,
  useRequisitionUpdateMutation,
} from '../../../hooks/react-query/useToolFundQuery';
import {
  useGetToolFundSettings,
  useToolFundSettingsUpdateMutation,
  useUserToolFundBalanceAvailableQuery,
} from '../../../hooks/react-query/useToolFundSettingsQuery';
import { useBatchUserQuery } from '../../../hooks/react-query/useUserQuery';
import { techniciansUserFilter } from '../../ComponentsLibrary/Pickers/newPickers/utils';
import { toolFundIneligibleLabel } from './employeeToolLog.utils';

/** The LMS course the tool fund training sends people to. */
export const TOOL_FUND_TRAINING_COURSE_URL =
  'https://www.kalosflorida.com/courses/using-the-kalos-tool-fund/';

/**
 * The Employee Tool Log's data, moved out of the desktop EmployeeToolLog so the phone screen runs
 * the same: the balance and settings, who may request tools (and what the button says when they
 * can't), the signed-in person's roles, the three request lists (pending, created by the person,
 * and created for them by someone else), the technicians and categories that name things, and
 * the page. Desktop reads every value as it did when this lived in the component.
 */
export const useEmployeeToolLog = ({ userId }: { userId: number }) => {
  const effectiveUserId = userId;
  const loggedUser = useAuth().user;
  const [page, setPage] = useState(0);
  // On: list requests by who pays. Off: by who created them.
  const isAdminPurchaseOn = useFeatureFlag('tool-fund.admin-purchase');

  const balanceQuery = useUserToolFundBalanceAvailableQuery(User.create({ id: effectiveUserId }));
  const { data: balance, isLoading: isLoadingBalance } = balanceQuery;

  const availableBalance = balance?.availableBalance ?? 0;

  const pendingRequestsQuery = useBatchGetRequisitionQuery({
    filters: {
      userId: effectiveUserId,
      statusId: 1,
      pageNumber: page,
      orderBy: 'requisition_id',
      orderDir: 'DESC',
      withItems: true,
      withFulfillments: true,
      withStatus: true,
      isActive: true,
    },
    fetchAllItems: true, // Fetch ALL items for each requisition (fixes 25-item limit)
  });

  // New queries for "All Requests" tab
  //this should be all things charged to (or, flag off, created by) the employee
  const allEmployeeRequestsQuery = useBatchGetRequisitionQuery({
    filters: {
      ...(isAdminPurchaseOn ? { chargedToEmpId: effectiveUserId } : { createdBy: effectiveUserId }),
      pageNumber: page,
      orderBy: 'requisition_id',
      orderDir: 'DESC',
      withItems: true,
      withFulfillments: true,
      withStatus: true,
      isActive: true, // Filter out deleted requests
    },
    fetchAllItems: true, // Fetch ALL items for each requisition (fixes 25-item limit)
  });
  //this should be all things for the employee that someone else pays for (or, flag off, created)
  const allManagerRequestsQuery = useBatchGetRequisitionQuery({
    filters: {
      userId: effectiveUserId,
      ...(isAdminPurchaseOn
        ? { fieldMask: ['!chargedToEmpId'], chargedToEmpId: effectiveUserId }
        : { fieldMask: ['!createdBy'], createdBy: effectiveUserId }),
      pageNumber: page,
      orderBy: 'requisition_id',
      orderDir: 'DESC',
      withItems: true,
      withFulfillments: true,
      withStatus: true,
      isActive: true, // Filter out deleted requests
    },
    fetchAllItems: true, // Fetch ALL items for each requisition (fixes 25-item limit)
  });

  const { data: techniciansData } = useBatchUserQuery({
    filters: techniciansUserFilter,
    select(data: { results: User[] }) {
      return new Map(data.results.map((u: User) => [u.id, u]));
    },
  });

  const isToolFundAdmin = loggedUser.permissionGroups.some((perm) => perm.name === 'ToolFundAdmin');
  const isManager = loggedUser.permissionGroups.some((group) => group.name === 'Manager');
  const isOwnLog = loggedUser.id === effectiveUserId;

  const categoriesQuery = useToolFundCatalogItemCategoryQuery({
    filter: { pageNumber: -1 },
  });

  const settingsQuery = useGetToolFundSettings({
    userId: effectiveUserId,
  });
  const { data: toolFundSettings, isLoading: isLoadingSettings } = settingsQuery;

  const isEligibleForToolFund = useMemo(() => {
    const hasPositiveBalance = availableBalance > 0;
    return (toolFundSettings?.isEligible ?? false) && hasPositiveBalance;
  }, [toolFundSettings, availableBalance]);

  /** Desktop opens the training dialog when the person's own settings say it isn't done. */
  const needsTrainingConfirmation =
    isOwnLog && !!toolFundSettings && toolFundSettings.trainingConfirmed === false;

  const isLoading = isLoadingBalance || isLoadingSettings;

  return {
    page,
    setPage,
    balanceQuery,
    isLoadingBalance,
    availableBalance,
    settingsQuery,
    toolFundSettings,
    isLoadingSettings,
    isEligibleForToolFund,
    ineligibleLabel: toolFundIneligibleLabel(toolFundSettings),
    needsTrainingConfirmation,
    isLoading,
    pendingRequestsQuery,
    allEmployeeRequestsQuery,
    allManagerRequestsQuery,
    techniciansData,
    categoriesQuery,
    isToolFundAdmin,
    isManager,
    isOwnLog,
  };
};

/**
 * The tool fund training confirmation, moved out of desktop's TrainingConfirmationDialog so the
 * phone sheet does the same: Confirm records the training as confirmed and opens the course;
 * Cancel leaves for the Employees page.
 */
export const useToolFundTrainingConfirmation = ({
  onOpenChange,
}: {
  onOpenChange: (open: boolean) => void;
}) => {
  const { user: loggedUser } = useAuth();
  const updateSettingsMutation = useToolFundSettingsUpdateMutation();
  const { data: toolFundSettings } = useGetToolFundSettings({
    userId: loggedUser.id,
  });

  const handleCancel = () => {
    onOpenChange(false);
    window.location.href = '/employees';
  };

  const handleConfirm = async () => {
    try {
      if (!toolFundSettings?.id) {
        throw new Error('Tool fund settings ID not found');
      }

      await updateSettingsMutation.mutateAsync(
        ToolFundSettings.create({
          id: toolFundSettings.id,
          userId: loggedUser.id,
          trainingConfirmed: true,
          trainingConfirmedDate: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
          fieldMask: ['TrainingConfirmed', 'TrainingConfirmedDate'],
        }),
      );
      onOpenChange(false);
      window.open(TOOL_FUND_TRAINING_COURSE_URL, '_blank');
    } catch (err) {
      console.error('Failed to update training confirmation:', err);
      toast({
        title: 'Error',
        description: 'Failed to update training status',
        variant: 'destructive',
      });
    }
  };

  return { handleCancel, handleConfirm, isPending: updateSettingsMutation.isPending };
};

const reassignSchema = z.object({
  employeeId: z.number().min(1, { message: 'Please select an employee' }),
});

const defaultReassignValues = {
  employeeId: 0,
};

export type ReassignFormValues = z.infer<typeof reassignSchema>;

/**
 * Desktop's Reassign Request form, moved out of its ReassignDialog so the phone sheet sends the
 * same update (the request's employee) with the same messages.
 */
export const useReassignRequestForm = ({
  requisition,
  onOpenChange,
}: {
  requisition: Requisition;
  onOpenChange?: (open: boolean) => void;
}) => {
  const form = useForm<ReassignFormValues>({
    resolver: zodResolver(reassignSchema),
    defaultValues: defaultReassignValues,
  });

  const handleOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange?.(open);
      if (!open) {
        form.reset(defaultReassignValues);
      }
    },
    [onOpenChange, form],
  );

  const requisitionUpdateMutation = useRequisitionUpdateMutation();

  const handleReassign = async (values: ReassignFormValues) => {
    if (!values.employeeId) return;

    try {
      await requisitionUpdateMutation.mutateAsync(
        Requisition.create({
          id: requisition.id,
          userId: Number(values.employeeId),
          fieldMask: ['UserId'],
        }),
      );

      toast({
        title: 'Success',
        description: 'Request reassigned successfully',
        variant: 'success',
      });

      handleOpenChange(false);
    } catch (err) {
      console.error('Failed to reassign requisition:', err);
      toast({
        title: 'Error',
        description: 'Failed to reassign request',
        variant: 'destructive',
      });
    }
  };

  return { form, handleOpenChange, handleReassign, requisitionUpdateMutation };
};
